import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'ionic-appauth-custom';
import {from, Observable, Subject, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  BackendErrorEvent,
  BFood,
  BFoodProduct,
  BRecipe,
  IPagination,
  RegistrationUser,
  SuccessResponse
} from './DataModels/BackendModels';
import {IUser, TypicalMeal, User} from './DataModels/User';
import { IFactOfTheDay } from './DataModels/FactOfTheDay';
import { ITrackedDay } from './DataModels/TrackedDay';
import { IQuestionnaire } from './questionnaire/questionnaireClassesAndEnums';
import { IRecipe } from './DataModels/Recipe';
import { IFoodLog, IFoodRecord } from './DataModels/FoodRecord';
import { INotificationSettings } from './DataModels/NotificationSettings';
import { toJSONLocal } from './helpers/helpers';
import { INewsArticle } from './DataModels/NewsArticle';
import { IInfoHubFavorites } from './DataModels/InfoHubFavorites';
import {ChangeItemLog, IFoodHistory} from './DataModels/FoodHistory';
import { PlatformHelperService } from './helpers/platform-helper.service';
import {TutorialsDone} from './DataModels/TutorialsDone';
import {Browser} from '@capacitor/browser';
import {SharedRecipeResponse, ShareRecipeResponse} from './DataModels/SharedRecipe';
import {AppPlatform, AppVersion} from './DataModels/AppVersion';
import {IMissingItemReport, MissingItemReport, MissingItemReportResult} from './DataModels/MissingItemReport';
import {IRecipeCategoryHierarchyItem} from './DataModels/RecipeCategoryHierarchyItem';
import {RecordingData} from 'capacitor-voice-recorder';
import {ISpeechToMealResponse} from './DataModels/IngredientMapping/SpeechToMealResponse';
import {FeedbackQuestionResponse} from './DataModels/FeedbackQuestionResponse';
import { TokenResponse } from '@openid/appauth';


@Injectable({
  providedIn: 'root'
})
export class BackendService {

  public errorSubject: Subject<BackendErrorEvent> = new Subject<BackendErrorEvent>();

  // Backend-URLs
  private urlIdentityProviderOnline: string = environment.auth_config.server_host + '/api/Health/Online';
  private urlIdentityProviderOnlineAuth: string = environment.auth_config.server_host + '/api/Health/OnlineAuth';
  private urlFoodSearchOnline: string = environment.food_search_host + 'api/Health/Online';
  private urlFoodSearchOnlineAuth: string = environment.food_search_host + 'api/Health/OnlineAuth';
  private urlFindFood: string = environment.food_search_host + 'api/Food/Find';
  private urlGetFood: string = environment.food_search_host + 'api/Food/';
  private urlFindFoodProducts: string = environment.food_search_host + 'api/FoodProducts/Find?name=';
  private urlGetFoodProduct: string = environment.food_search_host + 'api/FoodProducts/';
  private urlFindRecipes: string = environment.food_search_host + 'api/Recipe?name=';
  private urlGetRecipe: string = environment.food_search_host + 'api/Recipe/';
  private urlGetIdoplantyRecipeImage: string = environment.food_search_host + 'api/Recipe/Image/';
  private urlUpdateRecipeLinkMetaData: string = environment.food_search_host + 'api/Recipe/UpdateLinkMetaData/';
  private urlGetRecipeCategoryHierarchyItems: string = environment.food_search_host + 'api/Recipe/CategoryHierarchyItem';
  private urlPostSpeechToMealRecord: string = environment.food_search_host + 'api/SpeechProcessing/SpeechToMeal';
  private urlBackendOnline: string = environment.idoplanty_backend_host + 'api/Health/Online';
  private urlBackendOnlineAuth: string = environment.idoplanty_backend_host + 'api/Health/OnlineAuth';
  private urlGetUser: string = environment.idoplanty_backend_host + 'api/User';
  private urlGetUserByEmail: string = environment.idoplanty_backend_host + 'api/User/GetUserByEmail/';
  private urlGetUserChangeId: string = environment.idoplanty_backend_host + 'api/User/ChangeId/';
  private urlDeleteUser: string = environment.idoplanty_backend_host + 'api/User/';
  private urlUpdateUser: string = environment.idoplanty_backend_host + 'api/User';
  private urlUpdateTrackedDay: string = environment.idoplanty_backend_host + 'api/User/TrackedDay/';
  private urlUpdateQuestionnaire: string = environment.idoplanty_backend_host + 'api/User/Questionnaire/';
  private urlUpdateTypicalMeals: string = environment.idoplanty_backend_host + 'api/User/TypicalMeals/';
  private urlUpdateTypicalDailyEmissions: string = environment.idoplanty_backend_host + 'api/User/TypicalDailyEmissions/';
  private urlUpdateDaysUsed: string = environment.idoplanty_backend_host + 'api/User/DaysUsed/';
  private urlUpdateRecipe: string = environment.idoplanty_backend_host + 'api/User/Recipe/';
  private urlDeleteRecipe: string = environment.idoplanty_backend_host + 'api/User/Recipe/';
  private urlUpdateFoodHistory: string = environment.idoplanty_backend_host + 'api/User/FoodHistory/';
  private urlUpdateFoodRecord: string = environment.idoplanty_backend_host + 'api/User/FoodRecord/';
  private urlDeleteFoodRecord: string = environment.idoplanty_backend_host + 'api/User/FoodRecord/';
  private urlAddFoodLog: string = environment.idoplanty_backend_host + 'api/User/FoodLog/';
  private urlUpdateFoodLog: string = environment.idoplanty_backend_host + 'api/User/UpdateFoodLog/';
  private urlDeleteFoodLog: string = environment.idoplanty_backend_host + 'api/User/FoodLog/';
  private urlUpdateNotificationSettings: string = environment.idoplanty_backend_host + 'api/User/NotificationSettings/';
  private urlUpdateInfoHubFavorites: string = environment.idoplanty_backend_host + 'api/User/InfoHubFavorites/';
  private urlUpdateTutorialsDone: string = environment.idoplanty_backend_host + 'api/User/TutorialsDone/';
  private urlUpdateFiveDaysAppUsageFeedbackShown: string = environment.idoplanty_backend_host + 'api/User/FiveDaysAppUsageFeedbackShown/';
  private urlPostUserProfileImage: string = environment.idoplanty_backend_host + 'api/User/ProfileImage/';
  private urlDownloadUserProfileImage: string = environment.idoplanty_backend_host + 'api/User/ProfileImage/';
  private urlPostRecipeImage: string = environment.idoplanty_backend_host + 'api/User/RecipeImage/';
  private urlDownloadRecipeImage: string = environment.idoplanty_backend_host + 'api/User/RecipeImage/';
  private urlIsEmailVerified: string = environment.idoplanty_backend_host + 'api/User/IsEmailVerified';
  private urlChangePassword: string = environment.auth_config.server_host + '/Account/ChangePassword';
  private urlChangeUnverifiedEmail: string = environment.auth_config.server_host + '/Account/ChangeUnverifiedMailAddress';
  private urlReSendVerificationMail: string = environment.auth_config.server_host + '/api/AccountAPI/ReSendVerificationMail/';
  private urlRegisterUser: string = environment.idoplanty_backend_host + 'api/User';
  private urlGetFoodFactOfTheDay: string = environment.idoplanty_backend_host + 'api/FactOfTheDay';
  private urlGetLastXFoodFactsOfTheDay: string = environment.idoplanty_backend_host + 'api/FactOfTheDay/GetLast';
  private urlGetNews: string = environment.idoplanty_backend_host + 'api/News';
  private urlShareRecipe: string = environment.idoplanty_backend_host + 'api/SharedRecipe/';
  private urlGetSharedRecipe: string = environment.idoplanty_backend_host + 'api/SharedRecipe/';
  private urlGetLatestAppVersion: string = environment.idoplanty_backend_host + 'api/AppVersion/';
  private urlGetAppVersions: string = environment.idoplanty_backend_host + 'api/AppVersion/Find?platform=';
  private urlCheckIfAppIsLatest: string = environment.idoplanty_backend_host + 'api/AppVersion/IsLatestVersion/';
  private urlCheckForMissingItemReports: string = environment.idoplanty_backend_host + 'api/MissingItemReport/CompletedUnseen';
  private urlCreateMissingItemReport: string = environment.idoplanty_backend_host + 'api/MissingItemReport';
  private urlGetMissingItemReports: string = environment.idoplanty_backend_host + 'api/MissingItemReport/Find';
  private urlSeenMissingItemReport: string = environment.idoplanty_backend_host + 'api/MissingItemReport/WasSeen/';
  private urlCreateFeedbackQuestionResponse: string = environment.idoplanty_backend_host + 'api/Feedback';

  private _user: User;

  constructor(private http: HttpClient, private auth: AuthService, private platformHelper: PlatformHelperService) {}

  public setUser(user: User): void {
    this._user = user;
  }

  // ************************************* */
  // *** Identity Provider
  // ************************************* */

  identityProviderOnline(): Observable<HttpResponse<boolean>> {
    return this.http.get<boolean>(this.urlIdentityProviderOnline, { observe: 'response' })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.identityProvider, false)));
  }

  // This one probably does not make sense as we are not logged in at the identity provider (and also don't need to be logged in)
  async identityProviderOnlineAuth(): Promise<Observable<HttpResponse<boolean>>> {
    const token: TokenResponse = await this.getValidToken();
    return this.http.get<boolean>(this.urlIdentityProviderOnlineAuth, { observe: 'response', headers: this.addHeaders(token) })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.identityProvider, false)));
  }

  // ************************************* */
  // *** Food
  // ************************************* */

  foodSearchOnline(): Observable<HttpResponse<boolean>> {
    return this.http.get<boolean>(this.urlFoodSearchOnline, { observe: 'response' })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.foodSearch, false)));
  }

  async foodSearchOnlineAuth(): Promise<Observable<HttpResponse<boolean>>> {
    const token: TokenResponse = await this.getValidToken();
    return this.http.get<boolean>(this.urlFoodSearchOnlineAuth, { observe: 'response', headers: this.addHeaders(token) })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.foodSearch, false)));
  }

  findFood(name?: string, categories?: string[], subCategories?: string[], searchId?: string):
      Observable<HttpResponse<BFood[]>> {
    let nameString = '';
    if (name && name !== '') { nameString = '&name=' + name; }
    let catString = '';
    if (categories && categories.length > 0){
      categories.forEach(cat => {
        catString += '&categories=' + cat;
      });
    }
    let subCatString = '';
    if (subCategories && subCategories.length > 0){
      subCategories.forEach(subCatat => {
        subCatString += '&subCategories=' + subCatat;
      });
    }
    const searchIdString = searchId ? '&searchId=' + searchId : '';
    let filterString: string = nameString + catString + subCatString + searchIdString;
    filterString = filterString.replace('&', '?');
    const backendRequest = (token: TokenResponse) =>
        this.http.get<BFood[]>(this.urlFindFood + filterString + this.anonymousUserString('&'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  getFood(id: string, forwardError: boolean = false): Observable<HttpResponse<BFood>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<BFood>(this.urlGetFood + id + this.anonymousUserString('?'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch, undefined, forwardError);
  }

  findFoodProduct(name: string, searchId?: string): Observable<HttpResponse<BFoodProduct[]>> {
    const searchIdString = searchId ? '&searchId=' + searchId : '';
    const backendRequest = (token: TokenResponse) =>
        this.http.get<BFoodProduct[]>(this.urlFindFoodProducts + name + searchIdString + this.anonymousUserString('&'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  getFoodProduct(id: string, searchId?: string, logSearch: boolean = true): Observable<HttpResponse<BFoodProduct>> {
    const searchIdString = searchId ? '?searchId=' + searchId : '';
    const logSearchString = (searchId ? '&' : '?') + 'logSearch=' + logSearch;
    const backendRequest = (token: TokenResponse) =>
        this.http.get<BFoodProduct>(this.urlGetFoodProduct + id + searchIdString + logSearchString + this.anonymousUserString('&'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch, false, true);
  }

  // ************************************* */
  // *** Recipes
  // ************************************* */

  findRecipes(name: string, searchId?: string, page = 0, pageSize = 100, logSearch = true)
      : Observable<HttpResponse<IPagination<BRecipe>>> {
    const searchIdString = searchId ? '&searchId=' + searchId : '';
    const paginationString = '&page=' + page + '&pageSize=' + pageSize;
    const logSearchString = '&logSearch=' + logSearch;
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IPagination<BRecipe>>(this.urlFindRecipes + name + searchIdString + paginationString
            + logSearchString + this.anonymousUserString('&'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  getRecipe(id: string): Observable<HttpResponse<BRecipe>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<BRecipe>(this.urlGetRecipe + id + this.anonymousUserString('?'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch, false, true);
  }

  downloadIdoplantyRecipeImage(id: string, index: number, size: number): Observable<HttpResponse<Blob>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get(this.urlGetIdoplantyRecipeImage + id + '/' + index + '/' + size + this.anonymousUserString('?'),
            { observe: 'response', responseType: 'blob', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  updateRecipeLinkMetaData(id: string): Observable<HttpResponse<void>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<void>(this.urlUpdateRecipeLinkMetaData + id + this.anonymousUserString('?'), undefined,
            { observe: 'response', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  getRecipeCategoryHierarchyItems(): Observable<HttpResponse<IRecipeCategoryHierarchyItem[]>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IRecipeCategoryHierarchyItem[]>(this.urlGetRecipeCategoryHierarchyItems + this.anonymousUserString('?'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.foodSearch);
  }

  // ************************************* */
  // *** Users
  // ************************************* */

  backendOnline(): Observable<HttpResponse<boolean>> {
    return this.http.get<boolean>(this.urlBackendOnline, { observe: 'response' })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.backend, false)));
  }

  async backendOnlineAuth(): Promise<Observable<HttpResponse<boolean>>> {
    const token: TokenResponse = await this.getValidToken();
    return this.http.get<boolean>(this.urlBackendOnlineAuth + this.anonymousUserString('?'),
        { observe: 'response', headers: this.addHeaders(token) })
      .pipe(catchError(error => this.handleError(error, BackendServiceInstance.backend, false)));
  }

  async registerNewUser(user: RegistrationUser): Promise<Observable<HttpResponse<RegistrationUser>>> {
    const hashedPassword: string = await this.sha256(user.password);
    const registerUser: RegistrationUser = {email: user.email, userName: user.userName, password: hashedPassword};
    console.log(registerUser);
    const backendRequest = () =>
        this.http.post<RegistrationUser>(this.urlRegisterUser, registerUser, { observe: 'response' });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, undefined, true);
  }

  getUser(): Observable<HttpResponse<IUser>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IUser>(this.urlGetUser, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  getUserByEmail(email: string): Observable<HttpResponse<IUser>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IUser>(this.urlGetUserByEmail + email, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  getUserChangeId(): Observable<HttpResponse<SuccessResponse>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<SuccessResponse>(this.urlGetUserChangeId, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  deleteUser(userId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.delete<string>(this.urlDeleteUser + userId, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateUser(user: IUser, changeId: string): Observable<HttpResponse<IUser>> {
    console.log(user);
    const backendRequest = (token: TokenResponse) =>
        this.http.put<IUser>(this.urlUpdateUser + '?changeId=' + changeId, user,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateTrackedDay(userId: string, trackedDay: ITrackedDay, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateTrackedDay + userId + '?changeId=' + changeId, trackedDay,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateQuestionnaire(userId: string, questionnaire: IQuestionnaire, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateQuestionnaire + userId + '?changeId=' + changeId, questionnaire,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateTypicalMeals(userId: string, typicalMeals: TypicalMeal[], changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateTypicalMeals + userId + '?changeId=' + changeId, typicalMeals,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateTypicalDailyEmissions(userId: string, typicalDailyEmissions: number, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateTypicalDailyEmissions + userId + '/' + typicalDailyEmissions
            + '?changeId=' + changeId, undefined, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateDaysUsed(userId: string, daysUsed: string[], changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateDaysUsed + userId + '?changeId=' + changeId, daysUsed,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateRecipe(userId: string, recipe: IRecipe, changeId: string): Observable<HttpResponse<SuccessResponse>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<SuccessResponse>(this.urlUpdateRecipe + userId + '?changeId=' + changeId, recipe,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  deleteRecipe(userId: string, recipeId: string, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.delete<string>(this.urlDeleteRecipe + userId + '/' + recipeId + '?changeId=' + changeId,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateFoodHistory(userId: string, foodHistory: IFoodHistory, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateFoodHistory + userId + '?changeId=' + changeId, foodHistory,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateFoodRecord(userId: string, foodRecord: IFoodRecord, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateFoodRecord + userId + '?changeId=' + changeId, foodRecord,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  deleteFoodRecord(userId: string, recordId: string, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.delete<string>(this.urlDeleteFoodRecord + userId + '/' + recordId + '?changeId=' + changeId,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  addFoodLog(userId: string, recordId: string, foodLog: IFoodLog, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlAddFoodLog + userId + '/' + recordId + '?changeId=' + changeId, foodLog,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateFoodLog(userId: string, recordId: string, foodLogs: ChangeItemLog, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateFoodLog + userId + '/' + recordId + '?changeId=' + changeId, foodLogs,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  deleteFoodLog(userId: string, recordId: string, foodLog: IFoodLog, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.request<string>('delete', this.urlDeleteFoodLog + userId + '/' + recordId + '?changeId=' + changeId,
            { body: foodLog, observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateNotificationSettings(userId: string, notificationSettings: INotificationSettings, changeId: string):
      Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateNotificationSettings + userId + '?changeId=' + changeId, notificationSettings,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateInfoHubFavorites(userId: string, favorites: IInfoHubFavorites, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateInfoHubFavorites + userId + '?changeId=' + changeId, favorites,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateTutorialsDone(userId: string, tutorialsDone: TutorialsDone, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateTutorialsDone + userId + '?changeId=' + changeId, tutorialsDone,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  updateFiveDaysAppUsageFeedbackShown(userId: string, changeId: string): Observable<HttpResponse<string>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlUpdateFiveDaysAppUsageFeedbackShown + userId + '?changeId=' + changeId, undefined,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  postUserProfileImage(userId: string, image: File): Observable<HttpResponse<string>> {
    const formData = new FormData();
    formData.append('file', image);
    const backendRequest = (token: TokenResponse) =>
        this.http.post<string>(this.urlPostUserProfileImage + userId, formData,
            { observe: 'response', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  downloadUserProfileImage(userId: string): Observable<HttpResponse<Blob>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get(this.urlDownloadUserProfileImage + userId,
            { observe: 'response', responseType: 'blob', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  postRecipeImage(userId: string, recipeId: string, image: File, changeId: string, mainImage: boolean = true):
      Observable<HttpResponse<SuccessResponse>> {
    const formData = new FormData();
    formData.append('file', image);
    const backendRequest = (token: TokenResponse) =>
        this.http.post<SuccessResponse>(this.urlPostRecipeImage + userId + '/' + recipeId + '?mainImage=' + mainImage
            + '&changeId=' + changeId, formData, { observe: 'response', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  downloadUserRecipeImage(userId: string, recipeId: string): Observable<HttpResponse<Blob>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get(this.urlDownloadRecipeImage + userId + '/' + recipeId,
            { observe: 'response', responseType: 'blob', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  isEmailVerified(): Observable<HttpResponse<boolean>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<boolean>(this.urlIsEmailVerified, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  goToChangePasswordPage(userId: string) {
    // Default case, app runs on mobile.
    let returnUrl: string = environment.app_id + '://settings';
    // In case the app runs in web (currently only dev) the return url is adjusted
    if (this.platformHelper.isWeb) { returnUrl = window.location.origin + '/settings'; }
    Browser.open({ url: this.urlChangePassword + '?returnUrl=' + returnUrl + '/account' + '&userId=' + userId,
      windowName: '_self' }).then();
  }

  goToChangeUnverifiedEmailPage(userId: string) {
    // Default case, app runs on mobile.
    let returnUrl: string = environment.app_id + '://settings';
    // In case the app runs in web (currently only dev) the return url is adjusted
    if (this.platformHelper.isWeb) { returnUrl = window.location.origin + '/settings'; }
    Browser.open({ url: this.urlChangeUnverifiedEmail + '?returnUrl=' + returnUrl + '&userId=' + userId, windowName: '_self' }).then();
  }

  reSendVerificationMail(userId: string): Observable<HttpResponse<any>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<any>(this.urlReSendVerificationMail + userId, { observe: 'response', headers: this.addHeaders(token, true) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.identityProvider);
  }

  // ************************************* */
  // *** Share recipe
  // ************************************* */

  getSharedRecipe(linkIdentifier: string): Observable<HttpResponse<SharedRecipeResponse>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<SharedRecipeResponse>(this.urlGetSharedRecipe + linkIdentifier,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, true, true);
  }

  postSharedRecipe(userId: string, recipeId: string): Observable<HttpResponse<ShareRecipeResponse>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<ShareRecipeResponse>(this.urlShareRecipe + userId + '/' + recipeId, undefined,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, true, true);
  }

  // ************************************* */
  // *** App version
  // ************************************* */

  getLatestAppVersion(platform: AppPlatform): Observable<HttpResponse<AppVersion>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<AppVersion>(this.urlGetLatestAppVersion + platform,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  getAppVersions(platform: AppPlatform): Observable<HttpResponse<AppVersion[]>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<AppVersion[]>(this.urlGetAppVersions + platform,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  isLatestAppVersion(platform: AppPlatform, major: number, minor: number, patch: number): Observable<HttpResponse<boolean>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<boolean>(this.urlCheckIfAppIsLatest + platform + '/' + major + '/' + minor + '/' + patch,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  // ************************************* */
  // *** Missing item reports
  // ************************************* */

  getCompletedUnseenMissingItemReports(): Observable<HttpResponse<IMissingItemReport[]>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IMissingItemReport[]>(this.urlCheckForMissingItemReports + this.anonymousUserString('?'),
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  postMissingItemReport(report: IMissingItemReport): Observable<HttpResponse<IMissingItemReport>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<IMissingItemReport>(this.urlCreateMissingItemReport, report,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, true, true);
  }

  seenMissingItemReport(id: string): Observable<HttpResponse<void>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<void>(this.urlSeenMissingItemReport + id, undefined,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, true, true);
  }

  getMissingItemReports(userId: string): Observable<HttpResponse<IPagination<IMissingItemReport>>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IPagination<IMissingItemReport>>(this.urlGetMissingItemReports + '?userId=' + userId,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

 // ************************************* */
  // *** Messages
  // ************************************* */

  async getMessages(userID: string): Promise<MissingItemReport[]> | undefined{
      // ************************************* */
      // *** logic to receive MissingItemReport[] from back end via userId goes here
      // ************************************* */

    // temporary for testing
    const messages: MissingItemReport[] = [];
    const result1 = new MissingItemReport();
    result1.itemName = 'potatoes au gratin';
    result1.teamComments = 'your item has been added!, thanks for your request!';
    result1.result = MissingItemReportResult.itemAdded;
    messages.push(result1);
    const result2 = new MissingItemReport();
    result2.itemName = 'dog';
    result2.teamComments = 'sorry, we could not find relevant emissions data for this request';
    result2.result = MissingItemReportResult.declined;
    messages.push(result2);
    const result3 = new MissingItemReport();
    result3.itemName = 'Wagner Veggie Schnitzel';
    result3.teamComments = 'your item has been added, thanks for your request!';
    result3.result = MissingItemReportResult.itemAdded;
    messages.push(result3);
    // return undefined;
    return messages;
  }

  async setMessages(missingItemReports: MissingItemReport[]){
    console.log(missingItemReports);
    // logic to update messages on backend ("wasSeen", "dateSeen" etc.)
  }

  // ************************************* */
  // *** Feedback question response
  // ************************************* */

  postFeedbackQuestionResponse(response: FeedbackQuestionResponse): Observable<HttpResponse<FeedbackQuestionResponse>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.post<FeedbackQuestionResponse>(this.urlCreateFeedbackQuestionResponse, response,
            { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend, true, true);
  }

  // ************************************* */
  // *** Food Fact of the Day
  // ************************************* */

  getFactOfTheDay(): Observable<HttpResponse<IFactOfTheDay>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IFactOfTheDay>(this.urlGetFoodFactOfTheDay, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  getLastXFactsOfTheDay(amount: number): Observable<HttpResponse<IFactOfTheDay[]>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<IFactOfTheDay[]>(this.urlGetLastXFoodFactsOfTheDay + '?userDate=' + toJSONLocal(new Date())
            + '&amount=' + amount, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  // ************************************* */
  // *** News
  // ************************************* */

  getNews(): Observable<HttpResponse<INewsArticle[]>> {
    const backendRequest = (token: TokenResponse) =>
        this.http.get<INewsArticle[]>(this.urlGetNews, { observe: 'response', headers: this.addHeaders(token) });
    return this.makeBackendRequest(backendRequest, BackendServiceInstance.backend);
  }

  // ************************************* */
  // *** Shared methods
  // ************************************* */

  private makeBackendRequest<T>(
      httpCall: (token: TokenResponse) => Observable<HttpResponse<T>>,
      backendServiceInstance: BackendServiceInstance,
      triggerErrorEvent: boolean = true,
      forwardError: boolean = false
  ): Observable<HttpResponse<T>> {
    return from(this.getValidToken()).pipe(
        switchMap(token => httpCall(token)),
        catchError(error => this.handleError(error, backendServiceInstance, triggerErrorEvent, forwardError))
    );
  }

  private addHeaders(token: TokenResponse, media: boolean = false) {
    return (token) ? !(media) ? {
                Authorization: `${(token.tokenType === 'bearer') ? 'Bearer' : token.tokenType} ${token.accessToken}`,
                'Content-Type': 'application/json'
            }
            : {
              Authorization: `${(token.tokenType === 'bearer') ? 'Bearer' : token.tokenType} ${token.accessToken}`
          }
          : {};

  }

  private async getValidToken(): Promise<TokenResponse> {
    return await this.retryAsync(this.auth.getValidToken.bind(this.auth), 3, 500);
  }

  private retryAsync<T>(
      asyncFunc: () => Promise<T>,
      retries: number,
      delay: number
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const attempt = (n: number) => {
        asyncFunc()
            .then(resolve)
            .catch((error) => {
              console.log('Caught! n: ' + n);
              console.log(error);
              if (n > 0) {
                setTimeout(() => attempt(n - 1), delay);
              } else {
                reject(error);
              }
            });
      };
      attempt(retries);
    });
  }

  private handleError(error: HttpErrorResponse, service: BackendServiceInstance, triggerEvent: boolean = true,
                      forwardError: boolean = false) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was:`);
      console.error(error.error);
    }
    // throw an error event which other services can subscribe on and take action accordingly
    // e.g. connectivity service to check internet connection
    if (triggerEvent) {
      this.errorSubject.next({event: error, service});
    }
    if (forwardError) {
      return throwError(() => error.error);
    }
    // return an ErrorObservable with a user-facing error message
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private anonymousUserString(chainCharacter: string): string {
    return this._user && this._user.isLocalUser ? chainCharacter + 'anonymousUserId=' + this._user.id : '';
  }

  private async sha256(text: string): Promise<string> {
    const uint8 = new TextEncoder().encode(text);
    const hashBuffer = await window.crypto.subtle.digest(
      {
          name: 'SHA-256',
      },
      uint8
    );
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}

export enum BackendServiceInstance {
  identityProvider, foodSearch, backend
}
